import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";

import { getActiveBanners, SystemBanner as SystemBannerType } from "Network/Core/SystemBanners/systemBanners";
import HeaderBanner from "./HeaderBanner";
import { useTranslationCheck } from "Hooks/useTranslationCheck";
import { setSystemBanners } from "Store/Actions/systemBannerActionCreator";
import toast from "react-hot-toast";

const SystemBanner: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslationCheck();
  const bannerRef = React.useRef<HTMLDivElement>(null);
  const banners = useSelector(
    (state: any) => state?.systemBannerReducer?.banners ?? []
  );

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await getActiveBanners();
        if (Array.isArray(response?.data)) {
          dispatch(setSystemBanners(response.data));
        } else {
          dispatch(setSystemBanners([]));
        }
      } catch (error) {
        toast.error(t("Translation.error_fetching_banners") || "Error fetching banners");
      }
    };
    fetchBanners();
  }, [dispatch]);

  useEffect(() => {
    const el = bannerRef.current;
    const hasBanners = Array.isArray(banners) && banners.length > 0;

    if (!hasBanners || !el) {
      document.documentElement.style.setProperty("--system-banner-height", "0px");
      return;
    }

    let rafId: number;
    const updateHeight = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const visibleHeight = rect.bottom > 0 ? Math.max(0, rect.bottom) : 0;
        document.documentElement.style.setProperty(
          "--system-banner-height",
          `${visibleHeight}px`
        );
      });
    };

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(el);
    window.addEventListener("scroll", updateHeight, true);
    updateHeight();

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener("scroll", updateHeight, true);
    };
  }, [banners]);
  
  if (!Array.isArray(banners) || banners.length === 0) {
    return null;
  }

  return (
    <Box ref={bannerRef}>
      {banners.map((banner: SystemBannerType) => (
        <HeaderBanner key={banner.id} banner={banner} />
      ))}
    </Box>
  );
};

export default SystemBanner;